import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { KpiCard, Pill, EmptyState, LoadingDots } from '../ui';
import { fmtCompact, fmtUsd } from '../../utils/formatNumbers';
import {
  listChannelIntelligenceChannels,
  getChannelIntelligenceProfile,
  listCreatorGroups,
  getCreatorIntelligenceProfile,
  getLiveNowSessions,
  ChannelIntelligenceListItem,
  ChannelIntelligenceProfile,
  CreatorGroupInfo,
  CreatorIntelligenceProfile,
  LiveNowSession,
} from '../../services/channelIntelligenceApi';

const fmtMonth = (m: string): string => {
  const x = /^(\d{4})-(\d{2})$/.exec(m);
  if (!x) return m;
  const names = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${names[+x[2] - 1]} ${x[1].slice(2)}`;
};

const card: React.CSSProperties = {
  background: 'var(--bg-surface-1)', border: '1px solid var(--border-1)',
  borderRadius: 'var(--radius-lg)', padding: 16, marginTop: 16,
};
const h3: React.CSSProperties = { fontSize: 14, fontWeight: 700, margin: '0 0 4px', color: 'var(--fg-1)' };
const th: React.CSSProperties = { textAlign: 'left', fontSize: 11, textTransform: 'uppercase', color: 'var(--fg-4)', padding: '6px 10px', borderBottom: '1px solid var(--border-1)', whiteSpace: 'nowrap' };
const td: React.CSSProperties = { fontSize: 13, padding: '6px 10px', borderBottom: '1px solid var(--border-2)', color: 'var(--fg-2)' };
const cardCaption: React.CSSProperties = { fontSize: 11.5, color: 'var(--fg-4)', margin: '0 0 12px', lineHeight: 1.45 };

/** Red pulsing LIVE pill shown when the live-stream monitor sees an active broadcast. */
const LiveBadge: React.FC<{ session: LiveNowSession }> = ({ session }) => (
  <span
    title={`${session.title} — ${fmtCompact(session.lastConcurrentViewers)} watching now (peak ${fmtCompact(session.peakConcurrentViewers)})`}
    style={{
      display: 'inline-flex', alignItems: 'center', gap: 5, padding: '2px 8px',
      borderRadius: 999, background: 'var(--danger-soft)',
      color: 'var(--danger)', fontSize: 11, fontWeight: 700, letterSpacing: 0.5,
    }}
  >
    <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--danger)' }} />
    LIVE · {fmtCompact(session.lastConcurrentViewers)} watching
  </span>
);

const ChannelProfilePanel: React.FC = () => {
  const [channels, setChannels] = useState<ChannelIntelligenceListItem[]>([]);
  const [creators, setCreators] = useState<CreatorGroupInfo[]>([]);
  const [liveNow, setLiveNow] = useState<LiveNowSession[]>([]);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [profile, setProfile] = useState<ChannelIntelligenceProfile | null>(null);
  const [creatorProfile, setCreatorProfile] = useState<CreatorIntelligenceProfile | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    listChannelIntelligenceChannels(undefined, 1000)
      .then((r) => setChannels(r.channels))
      .catch((e) => setError(e?.message || 'Failed to load channels'));
    listCreatorGroups()
      .then((r) => setCreators(r.creators))
      .catch(() => setCreators([]));
    getLiveNowSessions()
      .then((r) => setLiveNow(r.live))
      .catch(() => setLiveNow([]));
  }, []);

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = q ? channels.filter((c) => c.channelName.toLowerCase().includes(q) || c.channelId.toLowerCase() === q) : channels;
    return list.slice(0, 40);
  }, [channels, query]);

  const filteredCreators = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? creators.filter((c) => c.displayName.toLowerCase().includes(q) || c.key.includes(q)) : creators;
  }, [creators, query]);

  const pick = async (c: ChannelIntelligenceListItem) => {
    setQuery(c.channelName);
    setOpen(false);
    setLoading(true);
    setError(null);
    setProfile(null);
    setCreatorProfile(null);
    try {
      const r = await getChannelIntelligenceProfile(c.channelId);
      setProfile(r.profile);
    } catch (e: any) {
      setError(e?.message || 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const pickCreator = async (c: CreatorGroupInfo) => {
    setQuery(c.displayName);
    setOpen(false);
    setLoading(true);
    setError(null);
    setProfile(null);
    setCreatorProfile(null);
    try {
      const r = await getCreatorIntelligenceProfile(c.key);
      setCreatorProfile(r.profile);
    } catch (e: any) {
      setError(e?.message || 'Failed to load creator profile');
    } finally {
      setLoading(false);
    }
  };

  // Merge YT + ad + earnings months into one trend dataset keyed by month.
  // Works for both the single-channel profile and the creator-merged sections.
  const buildTrend = (sections: { youtube: ChannelIntelligenceProfile['youtube']; ads: ChannelIntelligenceProfile['ads']; earnings: ChannelIntelligenceProfile['earnings'] } | null) => {
    if (!sections) return [];
    const byMonth = new Map<string, any>();
    const ensure = (m: string) => {
      let r = byMonth.get(m);
      if (!r) { r = { month: m, label: fmtMonth(m) }; byMonth.set(m, r); }
      return r;
    };
    sections.youtube.months.forEach((m) => { const r = ensure(m.month); r.views = m.views; r.watchHrs = m.watchTimeHours; });
    sections.ads.months.forEach((m) => { const r = ensure(m.month); r.adImpressions = m.impressions; r.adRevenue = m.revenueUsd; });
    sections.earnings.months.forEach((m) => { const r = ensure(m.month); r.totalRevenue = m.totalUsd; });
    return Array.from(byMonth.values()).sort((a, b) => a.month.localeCompare(b.month));
  };
  const trend = useMemo(() => buildTrend(profile), [profile]);
  const creatorTrend = useMemo(() => buildTrend(creatorProfile?.merged || null), [creatorProfile]);

  return (
    <div>
      {/* Typeahead */}
      <div ref={boxRef} style={{ position: 'relative', maxWidth: 460 }}>
        <input
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder="Search a channel by name or UC id…"
          style={{
            width: '100%', padding: '10px 12px', fontSize: 14, borderRadius: 'var(--radius-md)',
            border: '1px solid var(--border-1)', background: 'var(--bg-surface-1)', color: 'var(--fg-1)',
          }}
        />
        {open && (filtered.length > 0 || filteredCreators.length > 0) && (
          <div style={{
            position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 20, marginTop: 4,
            background: 'var(--bg-surface-1)', border: '1px solid var(--border-1)', borderRadius: 'var(--radius-md)',
            boxShadow: 'var(--shadow-md)', maxHeight: 340, overflowY: 'auto',
          }}>
            {filteredCreators.length > 0 && (
              <div style={{ padding: '6px 12px 2px', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--fg-4)' }}>
                Creators (all channels)
              </div>
            )}
            {filteredCreators.map((c) => {
              const liveSession = liveNow.find((s) => s.creatorKey === c.key);
              return (
                <button
                  key={`creator-${c.key}`}
                  onClick={() => pickCreator(c)}
                  style={{
                    display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'center',
                    gap: 8, padding: '8px 12px', background: 'none', border: 'none', cursor: 'pointer',
                    textAlign: 'left', color: 'var(--fg-1)', fontSize: 13, fontWeight: 600,
                  }}
                >
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.displayName}</span>
                  <span style={{ display: 'flex', gap: 4, flexShrink: 0, alignItems: 'center' }}>
                    {liveSession && <LiveBadge session={liveSession} />}
                    <Pill>{c.channels.length} channels</Pill>
                  </span>
                </button>
              );
            })}
            {filteredCreators.length > 0 && filtered.length > 0 && (
              <div style={{ padding: '6px 12px 2px', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--fg-4)', borderTop: '1px solid var(--border-2)' }}>
                Channels
              </div>
            )}
            {filtered.map((c) => (
              <button
                key={c.channelId}
                onClick={() => pick(c)}
                style={{
                  display: 'flex', width: '100%', justifyContent: 'space-between', alignItems: 'center',
                  gap: 8, padding: '8px 12px', background: 'none', border: 'none', cursor: 'pointer',
                  textAlign: 'left', color: 'var(--fg-1)', fontSize: 13,
                }}
              >
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.channelName}</span>
                <span style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                  {c.hasDirectSold && <Pill>DS</Pill>}
                  {c.hasAdSense && <Pill>AS</Pill>}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>

      {loading && <div style={{ marginTop: 24 }}><LoadingDots /></div>}
      {error && <div style={{ marginTop: 16, color: 'var(--danger)' }}>{error}</div>}
      {!loading && !error && !profile && !creatorProfile && (
        <div style={{ marginTop: 24 }}>
          <EmptyState title="Pick a channel or creator" subtitle="Search above to see a unified profile: YouTube performance, advertisers, campaigns, audience, and revenue. Creators roll up all of their channels." />
        </div>
      )}

      {creatorProfile && !loading && (
        <div>
          {/* Creator header */}
          <div style={{ marginTop: 16, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <h2 style={{ margin: 0, fontSize: 20, color: 'var(--fg-1)' }}>{creatorProfile.displayName}</h2>
            <Pill>{creatorProfile.channels.length} channels combined</Pill>
            {liveNow.filter((s) => s.creatorKey === creatorProfile.creatorKey).map((s) => (
              <LiveBadge key={s.videoId} session={s} />
            ))}
            <span style={{ fontSize: 12, color: 'var(--fg-4)' }}>
              {fmtMonth(creatorProfile.coverage.from)}–{fmtMonth(creatorProfile.coverage.to)}
            </span>
          </div>

          <div style={{ marginTop: 6, fontSize: 12, color: 'var(--fg-4)', lineHeight: 1.5, maxWidth: 840 }}>
            All of this creator's channels merged: <strong>YouTube Analytics</strong> + <strong>Direct Sold ads</strong> +
            <strong> booked revenue</strong>, summed by month. Monthly data covers completed months — for live event windows
            (e.g. the World Cup) use the Creator Event Report via Ask Genius.
          </div>

          {/* Merged KPIs */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12, marginTop: 14 }}>
            <KpiCard label="YouTube Views" value={creatorProfile.merged.youtube.source === 'none' ? '—' : fmtCompact(creatorProfile.merged.youtube.totals.views)} hint="All channels · all views in range" />
            <KpiCard label="Watch Hours" value={creatorProfile.merged.youtube.source === 'none' ? '—' : fmtCompact(creatorProfile.merged.youtube.totals.watchTimeHours)} hint="All channels · total watch time (hrs)" />
            <KpiCard label="Net Subscribers" value={creatorProfile.merged.youtube.source === 'none' ? '—' : fmtCompact(creatorProfile.merged.youtube.totals.netSubscribers)} hint="All channels · gained − lost in range" />
            <KpiCard label="Direct Sold Impressions" value={fmtCompact(creatorProfile.merged.ads.totals.impressions)} hint="Direct Sold ads only · all channels" />
            <KpiCard label="Advertisers" value={creatorProfile.merged.ads.totals.advertiserCount} hint="Distinct advertisers across all channels" />
            <KpiCard label="Total Revenue" value={fmtUsd(creatorProfile.merged.earnings.totals.totalUsd)} featured hint={`Direct Sold ${fmtUsd(creatorProfile.merged.earnings.totals.directSoldUsd)} + AdSense ${fmtUsd(creatorProfile.merged.earnings.totals.adsenseUsd)}`} />
          </div>

          {/* Per-channel breakdown */}
          <div style={card}>
            <h3 style={h3}>By channel</h3>
            <div style={cardCaption}>Each channel's contribution to the combined totals above. Click nothing here — search a channel by name for its full profile.</div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr>
                  <th style={th}>Channel</th><th style={th}>YT Views</th><th style={th}>Watch Hrs</th>
                  <th style={th}>Net Subs</th><th style={th}>DS Impressions</th><th style={th}>Booked $</th>
                </tr></thead>
                <tbody>
                  {creatorProfile.perChannel.map((p) => (
                    <tr key={p.channelId}>
                      <td style={{ ...td, fontWeight: 600 }}>{p.channelName}</td>
                      <td style={td}>{p.youtube.source === 'none' ? '—' : fmtCompact(p.youtube.totals.views)}</td>
                      <td style={td}>{p.youtube.source === 'none' ? '—' : fmtCompact(p.youtube.totals.watchTimeHours)}</td>
                      <td style={td}>{p.youtube.source === 'none' ? '—' : fmtCompact(p.youtube.totals.netSubscribers)}</td>
                      <td style={td}>{fmtCompact(p.ads.totals.impressions)}</td>
                      <td style={td}>{fmtUsd(p.earnings.totals.totalUsd)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Merged trend */}
          {creatorTrend.length > 0 && (
            <div style={card}>
              <h3 style={h3}>Combined monthly trend</h3>
              <div style={cardCaption}>
                <strong>Left axis</strong> = counts: YouTube Views (all channels) and Direct Sold ad impressions.
                <strong> Right axis</strong> = Revenue $ (Direct Sold + AdSense). Different denominators — shown together only for shape.
              </div>
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={creatorTrend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border-1)" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                  <YAxis yAxisId="left" tick={{ fontSize: 11 }} tickFormatter={fmtCompact} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} tickFormatter={(v) => `$${fmtCompact(v)}`} />
                  <Tooltip />
                  <Legend />
                  <Line yAxisId="left" type="monotone" dataKey="views" name="YouTube Views" stroke="var(--success)" strokeWidth={2} dot={false} />
                  <Line yAxisId="left" type="monotone" dataKey="adImpressions" name="Direct Sold Impr." stroke="var(--accent)" strokeWidth={2} dot={false} />
                  <Line yAxisId="right" type="monotone" dataKey="totalRevenue" name="Revenue (DS + AdSense)" stroke="var(--viz-2)" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Merged advertisers */}
          {creatorProfile.merged.ads.advertisers.length > 0 && (
            <div style={card}>
              <h3 style={h3}>Advertisers across all channels · Direct Sold ({creatorProfile.merged.ads.totals.advertiserCount})</h3>
              <div style={cardCaption}>Direct Sold campaigns only, merged across every channel in the group.</div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead><tr>
                    <th style={th}>Advertiser</th><th style={th}>Months</th>
                    <th style={th}>Impressions</th><th style={th}>Clicks</th><th style={th}>Media Sales $</th>
                  </tr></thead>
                  <tbody>
                    {creatorProfile.merged.ads.advertisers.slice(0, 30).map((a) => (
                      <tr key={a.advertiserId || a.advertiser}>
                        <td style={td}>{a.advertiser}</td>
                        <td style={td}>{a.months.length}</td>
                        <td style={td}>{fmtCompact(a.impressions)}</td>
                        <td style={td}>{fmtCompact(a.clicks)}</td>
                        <td style={td}>{fmtUsd(a.revenueUsd)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Audience (largest channel) */}
          {creatorProfile.merged.youtube.latestAudience && (
            <AudienceCard audience={creatorProfile.merged.youtube.latestAudience} />
          )}
        </div>
      )}

      {profile && !loading && (
        <div>
          {/* Header */}
          <div style={{ marginTop: 16, display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
            <h2 style={{ margin: 0, fontSize: 20, color: 'var(--fg-1)' }}>{profile.channelName}</h2>
            <code style={{ fontSize: 12, color: 'var(--fg-4)' }}>{profile.channelId}</code>
            {profile.metadata.category && <Pill>{profile.metadata.category}</Pill>}
            {profile.metadata.cms && <Pill>{profile.metadata.cms}</Pill>}
            <span style={{ fontSize: 12, color: 'var(--fg-4)' }}>
              {fmtMonth(profile.coverage.from)}–{fmtMonth(profile.coverage.to)}
            </span>
          </div>

          <div style={{ marginTop: 6, fontSize: 12, color: 'var(--fg-4)', lineHeight: 1.5, maxWidth: 840 }}>
            Joins three sources for this channel: <strong>YouTube Analytics</strong> (views · watch time · subscribers — all traffic),
            <strong> Direct Sold ads</strong> (GAM-sold impressions &amp; media sales), and <strong>booked revenue</strong> (Direct
            Sold + AdSense). Figures from different sources aren't the same denominator — e.g. YouTube views ≠ Direct Sold impressions.
          </div>

          {/* KPIs */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12, marginTop: 14 }}>
            <KpiCard label="YouTube Views" value={profile.youtube.source === 'none' ? '—' : fmtCompact(profile.youtube.totals.views)} hint="YouTube · all views in range" />
            <KpiCard label="Watch Hours" value={profile.youtube.source === 'none' ? '—' : fmtCompact(profile.youtube.totals.watchTimeHours)} hint="YouTube · total watch time (hrs)" />
            <KpiCard label="Net Subscribers" value={profile.youtube.source === 'none' ? '—' : fmtCompact(profile.youtube.totals.netSubscribers)} hint="YouTube · gained − lost in range" />
            <KpiCard label="Direct Sold Impressions" value={fmtCompact(profile.ads.totals.impressions)} hint="Direct Sold ads only · not YouTube views, not AdSense" />
            <KpiCard label="Advertisers" value={profile.ads.totals.advertiserCount} hint="Distinct Direct Sold advertisers" />
            <KpiCard label="Total Revenue" value={fmtUsd(profile.earnings.totals.totalUsd)} featured hint={`Direct Sold ${fmtUsd(profile.earnings.totals.directSoldUsd)} + AdSense ${fmtUsd(profile.earnings.totals.adsenseUsd)}`} />
          </div>

          {profile.youtube.source === 'none' && (
            <div style={{ marginTop: 10, fontSize: 12, color: 'var(--fg-4)' }}>
              ⚠️ No captured YouTube monthly analytics for this channel in range yet — run a YouTube capture/backfill.
            </div>
          )}

          {/* Trend chart */}
          {trend.length > 0 && (
            <div style={card}>
              <h3 style={h3}>Monthly trend</h3>
              <div style={cardCaption}>
                <strong>Left axis</strong> = counts: YouTube Views (all traffic) and Direct Sold ad impressions.
                <strong> Right axis</strong> = Revenue $ (Direct Sold + AdSense). Three separate sources on one timeline — different denominators, shown together only for shape.
              </div>
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={trend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border-1)" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                  <YAxis yAxisId="left" tick={{ fontSize: 11 }} tickFormatter={fmtCompact} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} tickFormatter={(v) => `$${fmtCompact(v)}`} />
                  <Tooltip />
                  <Legend />
                  <Line yAxisId="left" type="monotone" dataKey="views" name="YouTube Views" stroke="var(--success)" strokeWidth={2} dot={false} />
                  <Line yAxisId="left" type="monotone" dataKey="adImpressions" name="Direct Sold Impr." stroke="var(--accent)" strokeWidth={2} dot={false} />
                  <Line yAxisId="right" type="monotone" dataKey="totalRevenue" name="Revenue (DS + AdSense)" stroke="var(--viz-2)" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Advertisers */}
          {profile.ads.advertisers.length > 0 && (
            <div style={card}>
              <h3 style={h3}>Advertisers · Direct Sold ({profile.ads.totals.advertiserCount})</h3>
              <div style={cardCaption}>Direct Sold campaigns only. Impressions and Media Sales $ are Direct Sold — AdSense is not advertiser-attributed.</div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead><tr>
                    <th style={th}>Advertiser</th><th style={th}>Months</th>
                    <th style={th}>Impressions</th><th style={th}>Clicks</th><th style={th}>Media Sales $</th>
                  </tr></thead>
                  <tbody>
                    {profile.ads.advertisers.slice(0, 30).map((a) => (
                      <tr key={a.advertiserId || a.advertiser}>
                        <td style={td}>{a.advertiser}</td>
                        <td style={td}>{a.months.length}</td>
                        <td style={td}>{fmtCompact(a.impressions)}</td>
                        <td style={td}>{fmtCompact(a.clicks)}</td>
                        <td style={td}>{fmtUsd(a.revenueUsd)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Campaigns */}
          {profile.ads.campaigns.length > 0 && (
            <div style={card}>
              <h3 style={h3}>Campaigns · Direct Sold ({profile.ads.totals.campaignCount})</h3>
              <div style={cardCaption}>GAM orders behind this channel's Direct Sold delivery. Impressions are Direct Sold.</div>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead><tr>
                    <th style={th}>Campaign</th><th style={th}>Order #</th><th style={th}>Months</th><th style={th}>Impressions</th>
                  </tr></thead>
                  <tbody>
                    {profile.ads.campaigns.slice(0, 30).map((c) => (
                      <tr key={c.gamOrderId}>
                        <td style={td}>{c.campaignName || '(unnamed)'}</td>
                        <td style={{ ...td, fontFamily: 'monospace', fontSize: 12 }}>{c.gamOrderId}</td>
                        <td style={td}>{c.months.length}</td>
                        <td style={td}>{fmtCompact(c.impressions)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Audience */}
          {profile.youtube.latestAudience && (
            <AudienceCard audience={profile.youtube.latestAudience} />
          )}
        </div>
      )}
    </div>
  );
};

const AudienceCard: React.FC<{ audience: NonNullable<ChannelIntelligenceProfile['youtube']['latestAudience']> }> = ({ audience }) => {
  const hasAny = (audience.ageGender?.length || audience.geography?.length || audience.devices?.length || audience.trafficSources?.length);
  if (!hasAny) return null;
  return (
    <div style={card}>
      <h3 style={h3}>Audience · YouTube — {fmtMonth(audience.month)}</h3>
      <div style={cardCaption}>From YouTube Analytics for the latest captured month. Geography / Devices / Traffic values are YouTube views.</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 }}>
        {!!audience.ageGender?.length && (
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--fg-3)', marginBottom: 6 }}>Age × Gender</div>
            {audience.ageGender.slice(0, 8).map((d, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '2px 0', color: 'var(--fg-2)' }}>
                <span>{d.ageGroup.replace('age', '')} · {d.gender}</span><span>{d.viewerPercentage.toFixed(1)}%</span>
              </div>
            ))}
          </div>
        )}
        {!!audience.geography?.length && (
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--fg-3)', marginBottom: 6 }}>Top Geographies</div>
            {audience.geography.slice(0, 8).map((g, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '2px 0', color: 'var(--fg-2)' }}>
                <span>{g.country}</span><span>{fmtCompact(g.views)}</span>
              </div>
            ))}
          </div>
        )}
        {!!audience.devices?.length && (
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--fg-3)', marginBottom: 6 }}>Devices</div>
            {audience.devices.slice(0, 6).map((d, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '2px 0', color: 'var(--fg-2)' }}>
                <span>{d.deviceType}</span><span>{fmtCompact(d.views)}</span>
              </div>
            ))}
          </div>
        )}
        {!!audience.trafficSources?.length && (
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--fg-3)', marginBottom: 6 }}>Traffic Sources</div>
            {audience.trafficSources.slice(0, 6).map((t, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, padding: '2px 0', color: 'var(--fg-2)' }}>
                <span>{t.source}</span><span>{fmtCompact(t.views)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ChannelProfilePanel;
